import React, { useEffect, useState } from 'react';
import { Activity, Calendar, Send, Building, RefreshCw, History, CreditCard, CheckSquare, Square, AlertTriangle, XCircle, Briefcase, CheckCircle, Mail, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api, { authUrl } from '../api';
import { MonthContext } from '../context/MonthContext';

const Workflow = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const [checklist, setChecklist] = useState({});
  const [checklistDates, setChecklistDates] = useState({});
  const [activeClients, setActiveClients] = useState([]);
  const [anomalies, setAnomalies] = useState({ invoices_draft: [], invoices_pending: [], invoices_missing: [], invoices_disputed: [], expenses_local_error: [], expenses_axonaut_unreconciled: [] });
  const [employeesLeaves, setEmployeesLeaves] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [sendingPayroll, setSendingPayroll] = useState(false);
  const [generatingTimes, setGeneratingTimes] = useState({});
  const [expandedSections, setExpandedSections] = useState({ 1: true, 2: true, 3: true, 4: true, 5: true });
  const [navetteTo, setNavetteTo] = useState('paie@lexem-avocats.fr');
  const [navetteCc, setNavetteCc] = useState('gdejabrun@tallem.fr');
  const [navetteRemarques, setNavetteRemarques] = useState('');
  const [qontoStatements, setQontoStatements] = useState([]);
  const [sendingStatements, setSendingStatements] = useState(false);
  const [statementTo, setStatementTo] = useState('compta@septimeo.com');
  const [statementCc, setStatementCc] = useState('gdejabrun@tallem.fr');
  const [statementRemarques, setStatementRemarques] = useState('');

  const getFrenchHolidays = (year) => {
    const holidays = [
        { date: new Date(year, 0, 1), name: "Jour de l'An" },
        { date: new Date(year, 4, 1), name: "Fête du Travail" },
        { date: new Date(year, 4, 8), name: "Victoire 1945" },
        { date: new Date(year, 6, 14), name: "Fête Nationale" },
        { date: new Date(year, 7, 15), name: "Assomption" },
        { date: new Date(year, 10, 1), name: "Toussaint" },
        { date: new Date(year, 10, 11), name: "Armistice 1918" },
        { date: new Date(year, 11, 25), name: "Noël" }
    ];
    const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451), easterMonth = Math.floor((h + l - 7 * m + 114) / 31) - 1, easterDay = ((h + l - 7 * m + 114) % 31) + 1;
    const easter = new Date(year, easterMonth, easterDay);
    const easterMonday = new Date(easter); easterMonday.setDate(easter.getDate() + 1);
    holidays.push({ date: easterMonday, name: "Lundi de Pâques" });
    const ascension = new Date(easter); ascension.setDate(easter.getDate() + 39);
    holidays.push({ date: ascension, name: "Ascension" });
    const pentecostMonday = new Date(easter); pentecostMonday.setDate(easter.getDate() + 50);
    holidays.push({ date: pentecostMonday, name: "Lundi de Pentecôte" });
    return holidays;
  };

  const toggleAllSections = () => {
    const allExpanded = Object.values(expandedSections).every(v => v);
    setExpandedSections({ 1: !allExpanded, 2: !allExpanded, 3: !allExpanded, 4: !allExpanded, 5: !allExpanded });
  };
  
  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const sendStatementsToAccountant = async () => {
    if (!selectedMonth || selectedMonth === 'all') return;
    const stmts = qontoStatements; // déjà filtré sur le mois sélectionné
    if (stmts.length === 0) {
      alert("Aucun relevé trouvé pour ce mois.");
      return;
    }
    
    setSendingStatements(true);
    try {
      await api.post('/email/send-statements', {
        month_year: selectedMonth,
        to: statementTo,
        cc: statementCc,
        remarques: statementRemarques,
        statementIds: stmts.map(s => s.id)
      });
      alert('Les relevés bancaires ont été envoyés avec succès au comptable !');
      const taskId = 'qonto_statements_sent';
      if (!checklist[taskId]) {
        setChecklist(prev => ({ ...prev, [taskId]: true }));
        await api.post('/checklist', { task_id: taskId, month_year: selectedMonth, is_completed: true });
      }
    } catch (err) {
      console.error("Erreur lors de l'envoi des relevés:", err);
      alert('Erreur lors de l\'envoi des relevés.');
    } finally {
      setSendingStatements(false);
    }
  };

  const getSectionStats = (sectionId) => {
    let taskIds = [];
    switch (sectionId) {
      case 1:
        taskIds = ['fil_axonaut', 'fil_qonto', 'fil_outlook', 'fil_onedrive'];
        break;
      case 2:
        taskIds = ['j7_paie_manual', 'paie_tickets_restos', 'paie_salaires', 'paie_gerant', 'paie_notes_frais'];
        break;
      case 3:
        taskIds = ['fourn_verif_depenses', 'fourn_rapprochement'];
        break;
      case 4:
        taskIds = ['cli_refact_frais', 'cli_suivi_temps', 'cli_relance', 'cli_factures_emettre'];
        activeClients.forEach(c => {
          taskIds.push(`cli_${c.id}_temps`, `cli_${c.id}_frais`);
        });
        break;
      case 5:
        taskIds = ['qonto_statements_sent'];
        break;
      default:
        break;
    }
    const completed = taskIds.filter(id => !!checklist[id]).length;
    const total = taskIds.length;
    const isFinished = total > 0 && completed === total;
    
    return {
      text: `(${completed}/${total})`,
      pastille: isFinished ? '🟢' : '🔴',
      isFinished
    };
  };

  const { selectedMonth, setSelectedMonth, monthsStatus, setMonthsStatus } = React.useContext(MonthContext);
  const now = new Date();

  const [selYear, selMonth] = selectedMonth.split('-').map(Number);
  const lastDayOfMonth = new Date(selYear, selMonth, 0);
  const jMinus7 = new Date(lastDayOfMonth); jMinus7.setDate(jMinus7.getDate() - 7);
  const jMinus3 = new Date(lastDayOfMonth); jMinus3.setDate(jMinus3.getDate() - 3);

  const formatDate = (date) => date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

  const getWorkingDays = () => {
    if (!selectedMonth || selectedMonth === 'all') return { wDays: 0, monthHolidays: [] };
    const [year, month] = selectedMonth.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const holidays = getFrenchHolidays(year);
    const monthHols = holidays.filter(h => h.date.getMonth() === month - 1);
    const isHoliday = (date) => monthHols.some(h => h.date.getDate() === date.getDate());
    
    let wDays = 0;
    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday(date)) {
            wDays++;
        }
    }
    return { wDays, monthHolidays: monthHols };
  };

  const { wDays, monthHolidays } = getWorkingDays();

  const loadData = async (isPoll = false) => {
    try {
      const [checklistRes, clientsRes, anomaliesRes] = await Promise.all([
        api.get(`/checklist?month_year=${selectedMonth}`),
        api.get('/axonaut/active-clients'),
        api.get(`/axonaut/anomalies?month_year=${selectedMonth === 'all' ? '' : selectedMonth}`)
      ]);

      // Sur le rafraîchissement auto (poll), on NE réécrit PAS la checklist :
      // sinon une réponse en vol écrase une case que l'utilisateur vient de cocher
      // ("coche puis décoche"). La checklist n'est (re)chargée qu'au montage / changement de mois.
      if (!isPoll) {
        if (checklistRes.data && checklistRes.data.checklist) {
          setChecklist(checklistRes.data.checklist);
          setChecklistDates(checklistRes.data.dates || {});
        } else {
          setChecklist(checklistRes.data || {});
          setChecklistDates({});
        }
      }
      setActiveClients(clientsRes.data || []);
      setActiveClients(clientsRes.data || []);
      setAnomalies({ 
        invoices_draft: anomaliesRes.data?.invoices_draft || [], 
        invoices_pending: anomaliesRes.data?.invoices_pending || [], 
        invoices_missing: anomaliesRes.data?.invoices_missing || [], 
        invoices_disputed: anomaliesRes.data?.invoices_disputed || [],
        expenses_local_error: anomaliesRes.data?.expenses_local_error || [], 
        expenses_axonaut_unreconciled: anomaliesRes.data?.expenses_axonaut_unreconciled || [] 
      });

      // Relevés Qonto du mois sélectionné (period Qonto = "MM-YYYY")
      if (selectedMonth && selectedMonth !== 'all') {
        const [year, month] = selectedMonth.split('-');
        const qontoRes = await api.get('/qonto/statements');
        const stmts = qontoRes.data?.data || [];
        const monthKey = `${month}-${year}`;
        setQontoStatements(stmts.filter(s => s.period === monthKey));
      } else {
        setQontoStatements([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 10000);
    return () => clearInterval(interval);
  }, [selectedMonth]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoadingEmployees(true);
        const res = await api.get(`/payroll/employees?month_year=${selectedMonth}`);
        setEmployeesLeaves(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, [selectedMonth]);

  const toggleTask = async (taskId) => {
    const newState = !checklist[taskId];
    setChecklist(prev => ({ ...prev, [taskId]: newState }));
    try {
        await api.post('/checklist', { task_id: taskId, month_year: selectedMonth, is_completed: newState });
    } catch (err) {
        console.error("Erreur sauvegarde checklist", err);
        setChecklist(prev => ({ ...prev, [taskId]: !newState }));
    }
  };

  const CheckItem = ({ id, label, link, linkText, icon, action, actionText, actionLoading }) => {
    const isCompleted = !!checklist[id];
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', borderBottom: '1px solid var(--glass-border)', transition: 'all 0.2s', background: isCompleted ? 'rgba(16, 185, 129, 0.05)' : 'transparent' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTask(id); }}>
          {isCompleted ? <CheckSquare size={20} color="#10b981" /> : <Square size={20} color="var(--text-secondary)" />}
          {icon && <span style={{ display: 'flex', color: isCompleted ? 'var(--text-secondary)' : '#38bdf8' }}>{icon}</span>}
          <span style={{ fontSize: '0.95rem', color: isCompleted ? 'var(--text-secondary)' : 'var(--text-primary)', textDecoration: isCompleted ? 'line-through' : 'none' }}>{label}</span>
        </div>
        {link && (
            <div style={{ marginLeft: '1rem' }}>
              {link.startsWith('http') || link.startsWith('mailto') || link.startsWith('file:') ? (
                <a href={link} target={link.startsWith('file:') ? '_self' : '_blank'} rel="noopener noreferrer" style={{ display: 'inline-block', width: '160px', height: '28px', lineHeight: '28px', boxSizing: 'border-box', textAlign: 'center', fontSize: '0.8rem', padding: '0', background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', borderRadius: '4px', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{linkText}</a>
              ) : (
                <button onClick={() => navigate(link)} style={{ display: 'inline-block', width: '160px', height: '28px', lineHeight: '28px', boxSizing: 'border-box', textAlign: 'center', border: 'none', background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', padding: '0', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{linkText}</button>
              )}
            </div>
        )}
        {action && (
            <div style={{ marginLeft: '1rem' }}>
               <button onClick={action} disabled={actionLoading} style={{ display: 'inline-flex', justifyContent: 'center', alignItems: 'center', width: '160px', height: '28px', boxSizing: 'border-box', border: 'none', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '0', borderRadius: '4px', fontSize: '0.8rem', cursor: actionLoading ? 'not-allowed' : 'pointer', gap: '0.3rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                 {actionLoading ? <RefreshCw size={14} className="spinning" /> : null}
                 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{actionText}</span>
               </button>
            </div>
        )}
      </div>
    );
  };

  const MiniCheck = ({ id, label, action, actionLoading, actionText, link, linkText }) => {
    const isCompleted = !!checklist[id];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.35rem 0.5rem', borderRadius: '4px', background: isCompleted ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-primary)', border: '1px solid var(--glass-border)' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTask(id); }}>
        {isCompleted ? <CheckSquare size={16} color="#10b981" /> : <Square size={16} color="var(--text-secondary)" />}
        <span style={{ fontSize: '0.8rem', color: isCompleted ? 'var(--text-secondary)' : 'var(--text-primary)', textDecoration: isCompleted ? 'line-through' : 'none', whiteSpace: 'nowrap' }}>{label}</span>
        {action && (
            <button onClick={(e) => { e.stopPropagation(); action(); }} disabled={actionLoading} style={{ marginLeft: '0.5rem', border: 'none', background: 'var(--brand)', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', cursor: actionLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              {actionLoading ? <RefreshCw size={12} className="spinning" /> : actionText}
            </button>
        )}
        {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ marginLeft: '0.25rem', textDecoration: 'none', background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600 }}>
              {linkText || 'Axonaut'}
            </a>
        )}
      </div>
    );
  };

  const handleToggleCloseMonth = async () => {
    const isClosed = !!monthsStatus[selectedMonth];
    try {
      await api.post('/months/toggle-close', { month_year: selectedMonth, is_closed: !isClosed });
      setMonthsStatus(prev => ({ ...prev, [selectedMonth]: !isClosed }));

      if (!isClosed) {
        const allTaskIds = [
          'fil_axonaut', 'fil_qonto', 'fil_outlook', 'fil_onedrive',
          'j7_paie_manual', 'paie_tickets_restos', 'paie_salaires', 'paie_gerant', 'paie_notes_frais',
          'fourn_verif_depenses', 'fourn_rapprochement',
          'cli_refact_frais', 'cli_suivi_temps', 'cli_relance', 'cli_factures_emettre',
          ...activeClients.flatMap(c => [`cli_${c.id}_temps`, `cli_${c.id}_frais`])
        ];
        
        await api.post('/checklist/complete-all', {
          month_year: selectedMonth,
          task_ids: allTaskIds,
          is_completed: true
        });
        
        const updatedChecklist = { ...checklist };
        allTaskIds.forEach(id => updatedChecklist[id] = true);
        setChecklist(updatedChecklist);
      }
    } catch (e) {
      alert("Erreur lors de la modification du statut du mois.");
    }
  };

  const handleLeaveChange = (id, value) => {
    setEmployeesLeaves(prev => prev.map(emp => emp.id === id ? { ...emp, conges: value } : emp));
  };

  const handleLeaveDetailsChange = (id, value) => {
    setEmployeesLeaves(prev => prev.map(emp => emp.id === id ? { ...emp, manualLeaveDetails: value } : emp));
  };

  const handleSendNavette = async () => {
    if (employeesLeaves.length === 0) return;
    setSendingPayroll(true);
    try {
      await api.post('/payroll/send-navette', { 
        month_year: selectedMonth, 
        employees: employeesLeaves,
        to: navetteTo,
        cc: navetteCc,
        remarques: navetteRemarques
      });
      

      setChecklist(prev => ({ ...prev, 'j7_paie_manual': true }));
      setChecklistDates(prev => ({ ...prev, 'j7_paie_manual': new Date().toISOString() }));
      await api.post('/checklist', { task_id: 'j7_paie_manual', month_year: selectedMonth, is_completed: true });
    } catch (e) {
      alert("Erreur lors de l'envoi de la navette paie.");
    } finally {
      setSendingPayroll(false);
    }
  };

  const handleGenerateTimeReport = async (clientId, clientName) => {
    setGeneratingTimes(prev => ({...prev, [clientId]: true}));
    try {
      const res = await api.post('/projects/times', { clientId, clientName, monthYear: selectedMonth });
      if (res.data.success) {
        window.open(authUrl(res.data.pdfUrl), '_blank');
        setChecklist(prev => ({...prev, [`cli_${clientId}_temps`]: true}));
        await api.post('/checklist', { task_id: `cli_${clientId}_temps`, month_year: selectedMonth, is_completed: true });
      } else {
        alert(res.data.message || "Erreur de génération.");
      }
    } catch (e) {
      alert("Erreur lors de la génération du relevé de temps.");
    } finally {
      setGeneratingTimes(prev => ({...prev, [clientId]: false}));
    }
  };

  const areAllClientsChecked = () => {
    if (activeClients.length === 0) return false;
    for (const client of activeClients) {
      if (!checklist[`cli_${client.id}_temps`] || !checklist[`cli_${client.id}_frais`]) {
        return false;
      }
    }
    return true;
  };

  const toggleAllClients = async () => {
    if (activeClients.length === 0) return;
    const isChecked = areAllClientsChecked();
    const newChecklistState = { ...checklist };
    const apiCalls = [];

    activeClients.forEach(client => {
      const tempsId = `cli_${client.id}_temps`;
      const fraisId = `cli_${client.id}_frais`;
      
      if (!!newChecklistState[tempsId] === isChecked) {
        newChecklistState[tempsId] = !isChecked;
        apiCalls.push(api.post('/checklist', { task_id: tempsId, month_year: selectedMonth, is_completed: !isChecked }));
      }
      if (!!newChecklistState[fraisId] === isChecked) {
        newChecklistState[fraisId] = !isChecked;
        apiCalls.push(api.post('/checklist', { task_id: fraisId, month_year: selectedMonth, is_completed: !isChecked }));
      }
    });

    setChecklist(newChecklistState);
    if (apiCalls.length > 0) {
      try {
        await Promise.all(apiCalls);
      } catch (e) {
        console.error("Erreur toggle all clients", e);
      }
    }
  };

  const renderClientRow = (client) => {
    const isAlert = client.last_invoice_date && new Date(client.last_invoice_date) < new Date(selYear, selMonth - 1, 1) && !client.last_invoice_sent_date;
    return (
      <div key={client.id} style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', padding: '0.75rem 1rem', borderRadius: '8px', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          <MiniCheck
              id={`cli_${client.id}`}
              label="Traité"
              action={() => handleGenerateTimeReport(client.id, client.name)}
              actionText="PDF Temps"
              actionLoading={generatingTimes[client.id]}
              link={`https://axonaut.com/adm/company/view/${client.id}`}
              linkText="Axonaut"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 600, flex: 1, minWidth: '150px' }}>
          <Briefcase size={16} color={client.has_active_contract ? '#10b981' : 'var(--text-secondary)'} style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</span>
        </div>
        <div style={{ width: '380px', fontSize: '0.85rem', color: isAlert ? '#f43f5e' : 'var(--text-secondary)', fontWeight: isAlert ? 700 : 400, background: isAlert ? 'rgba(244, 63, 94, 0.05)' : 'var(--bg-primary)', padding: '0.35rem 0.75rem', borderRadius: '6px', border: `1px solid ${isAlert ? 'rgba(244, 63, 94, 0.2)' : 'var(--glass-border)'}`, whiteSpace: 'nowrap', flexShrink: 0 }}>
          {client.last_invoice_number ? (
            <>
              Facture : <a href={`https://axonaut.com/business/invoice/dashboard#openModalInvoiceSlide_${client.last_invoice_id}`} target="_blank" rel="noreferrer" style={{color: isAlert ? '#f43f5e' : 'var(--text-primary)', fontWeight: 600, textDecoration: 'none'}} className="hover-underline">{client.last_invoice_number}</a> du {new Date(client.last_invoice_date).toLocaleDateString('fr-FR')}
              <span style={{ margin: '0 0.5rem', color: 'var(--text-tertiary)' }}>|</span>
              Envoi : {client.last_invoice_sent_date ? new Date(client.last_invoice_sent_date).toLocaleDateString('fr-FR') : 'Non envoyée'}
            </>
          ) : 'Aucune facture'}
        </div>
      </div>
    );
  };

  if (loading) return <div className="spinner-overlay"><div className="spinner"></div><div style={{marginTop:'1rem', fontWeight:600, color:'var(--text-primary)'}}>Chargement...</div></div>;

  const allTaskIds = [
    'fil_axonaut', 'fil_qonto', 'fil_outlook', 'fil_onedrive', 'fil_dossier',
    'j7_paie_manual', 'paie_tickets_restos', 'paie_salaires', 'paie_gerant', 'paie_notes_frais',
    'fourn_verif_depenses', 'fourn_rapprochement',
    'cli_refact_frais', 'cli_suivi_temps', 'cli_relance', 'cli_factures_emettre',
    ...activeClients.flatMap(c => [`cli_${c.id}_temps`, `cli_${c.id}_frais`])
  ];
  
  const completedTasksCount = allTaskIds.filter(id => !!checklist[id]).length;
  const totalTasksCount = allTaskIds.length;
  const progressPercent = totalTasksCount > 0 ? Math.round((completedTasksCount / totalTasksCount) * 100) : 0;
  
  const pendingInvoicesCount = anomalies.invoices_draft.length + anomalies.invoices_pending.length + anomalies.invoices_missing.length;
  const pendingExpensesCount = anomalies.expenses_local_error.length + anomalies.expenses_axonaut_unreconciled.length;

  return (
    <div className="view-section">
      <div className="header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 0.5rem 0' }}>Workflow Mensuel</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>Gérez le processus de clôture du mois sélectionné.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={toggleAllSections} style={{ background: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-primary)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>
            Tout {Object.values(expandedSections).every(v => v) ? 'réduire' : 'déplier'}
          </button>
          <button 
            onClick={handleToggleCloseMonth}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: monthsStatus[selectedMonth] ? 'rgba(244, 63, 94, 0.1)' : 'rgba(16, 185, 129, 0.1)', color: monthsStatus[selectedMonth] ? '#f43f5e' : '#10b981', cursor: 'pointer', fontWeight: 600 }}
          >
            {monthsStatus[selectedMonth] ? 'Déclôturer ce mois' : 'Clôturer ce mois'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
        <div className="glass-panel" style={{ flex: '1 1 200px', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #10b981' }}>
          <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '0.75rem', borderRadius: '12px' }}>
             <CheckSquare size={24} color="#10b981" />
          </div>
          <div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{progressPercent}%</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Tâches terminées ({completedTasksCount}/{totalTasksCount})</div>
          </div>
        </div>
        
        <div className="glass-panel" style={{ flex: '1 1 200px', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #f43f5e' }}>
          <div style={{ background: 'rgba(244, 63, 94, 0.1)', padding: '0.75rem', borderRadius: '12px' }}>
             <Building size={24} color="#f43f5e" />
          </div>
          <div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{pendingExpensesCount}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Dépenses en attente</div>
          </div>
        </div>

        <div className="glass-panel" style={{ flex: '1 1 200px', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #f59e0b' }}>
          <div style={{ background: 'rgba(245, 158, 11, 0.1)', padding: '0.75rem', borderRadius: '12px' }}>
             <Send size={24} color="#f59e0b" />
          </div>
          <div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{pendingInvoicesCount}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Factures clients à traiter</div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '3rem' }}>
        <h4 onClick={() => toggleSection(2)} style={{ cursor: 'pointer', margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{getSectionStats(2).pastille}</span> <Calendar size={18} style={{ flexShrink: 0 }} /> 1. Ressources humaines <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>{getSectionStats(2).text}</span>
        </h4>
        {expandedSections[2] && <div className="glass-panel" style={{ padding: '1.5rem' }}>
          
          <div style={{ display: 'flex', flexDirection: 'column', padding: '0.75rem', borderBottom: '1px solid var(--glass-border)', transition: 'all 0.2s', background: checklist['j7_paie_manual'] ? 'rgba(16, 185, 129, 0.05)' : 'transparent' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, cursor: 'pointer' }} onClick={() => toggleTask('j7_paie_manual')}>
                {checklist['j7_paie_manual'] ? <CheckSquare size={20} color="#10b981" /> : <Square size={20} color="var(--text-secondary)" />}
                <span style={{ fontSize: '0.95rem', color: checklist['j7_paie_manual'] ? 'var(--text-secondary)' : 'var(--text-primary)', textDecoration: checklist['j7_paie_manual'] ? 'line-through' : 'none' }}>
                  Envoi navette paie & tickets restaurants
                </span>
              </div>
              <div style={{ marginLeft: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                {checklist['j7_paie_manual'] && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#10b981', fontSize: '0.85rem', fontWeight: 600, background: 'rgba(16, 185, 129, 0.1)', padding: '0.3rem 0.6rem', borderRadius: '6px' }}>
                    <CheckCircle size={14} /> Envoyé
                    {checklistDates['j7_paie_manual'] && (
                      <span style={{ fontWeight: 400, marginLeft: '0.2rem', opacity: 0.8 }}>
                        le {new Date(checklistDates['j7_paie_manual']).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    )}
                  </span>
                )}
                <button onClick={handleSendNavette} disabled={sendingPayroll || loadingEmployees} style={{ border: 'none', background: 'var(--accent)', color: 'white', padding: '0.4rem 1rem', borderRadius: '6px', fontSize: '0.85rem', cursor: sendingPayroll || loadingEmployees ? 'not-allowed' : 'pointer', opacity: sendingPayroll || loadingEmployees ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                  {sendingPayroll ? <><RefreshCw size={14} className="spinning"/> Envoi en cours...</> : <><Send size={14}/> Envoi navette</>}
                </button>
              </div>
            </div>
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--glass-border)', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
                <h5 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Détail des Congés & Absences</h5>
                {wDays > 0 && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Jours ouvrés ce mois : <strong>{wDays}</strong>
                    {monthHolidays.length > 0 && <span style={{ marginLeft: '0.5rem', opacity: 0.8 }}>(Fériés : {monthHolidays.map(h => `${h.date.getDate()}/${h.date.getMonth()+1}`).join(', ')})</span>}
                  </div>
                )}
              </div>
              {loadingEmployees ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}><RefreshCw className="spinning" size={16} /> Chargement des collaborateurs...</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  {employeesLeaves.map(emp => {
                    const tickets = Math.ceil(Math.max(0, wDays - parseFloat(emp.conges || 0)));
                    return (
                    <div key={emp.id} style={{ display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--glass-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{emp.name}</span>
                          <span style={{ fontSize: '0.7rem', color: '#10b981', fontWeight: 600 }}>{tickets} TR</span>
                        </div>
                        <input type="number" min="0" step="0.5" value={emp.conges} onChange={(e) => handleLeaveChange(emp.id, e.target.value)} style={{ width: '50px', padding: '0.2rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', textAlign: 'center', fontSize: '0.85rem' }} />
                      </div>
                      <input 
                        type="text" 
                        placeholder="Dates (ex: Du 12 au 14)" 
                        value={emp.manualLeaveDetails || ''} 
                        onChange={(e) => handleLeaveDetailsChange(emp.id, e.target.value)}
                        style={{ marginTop: '0.5rem', width: '100%', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: '0.75rem' }} 
                      />
                    </div>
                  );
                })}
                </div>
              )}
              
              <h5 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)', fontSize: '0.9rem', borderBottom: '1px solid var(--glass-border)', paddingBottom: '0.5rem' }}>Préparation de l'email</h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'var(--bg-primary)', padding: '1rem', borderRadius: '6px', border: '1px solid var(--glass-border)' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Destinataire (À)</label>
                    <input type="text" value={navetteTo} onChange={(e) => setNavetteTo(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>En copie (Cc)</label>
                    <input type="text" value={navetteCc} onChange={(e) => setNavetteCc(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                  </div>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Remarques (apparaîtront dans l'email)</label>
                  <textarea rows="3" value={navetteRemarques} onChange={(e) => setNavetteRemarques(e.target.value)} placeholder="Vos remarques ici..." style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical' }}></textarea>
                </div>

              </div>
            </div>
          </div>
          
          <CheckItem id="paie_tickets_restos" label="Achat des tickets restaurants" link="https://corpo.swile.co/pro/2d753242-ccf0-425b-8853-1dc295d0a84e/order/P6MA1A/refill-items" linkText="Swile" icon={<CreditCard size={18} />} />
          <CheckItem id="paie_salaires" label="Paiement des salaires" link="https://my.silae.fr/payroll/payslips-validation" linkText="Silae" icon={<CreditCard size={18} />} />
          <CheckItem id="paie_gerant" label="Paiement gérant" icon={<CreditCard size={18} />} />
          <CheckItem id="paie_notes_frais" label="Paiement des notes de frais" link="/expense-notes" linkText="Ouvrir NDF" icon={<CreditCard size={18} />} />

        </div>}
      </div>

      <div style={{ marginBottom: '3rem' }}>
        <h4 onClick={() => toggleSection(1)} style={{ cursor: 'pointer', margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{getSectionStats(1).pastille}</span> <Activity size={18} style={{ flexShrink: 0 }} /> 2. Fil de l'eau <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>{getSectionStats(1).text}</span>
        </h4>
        {expandedSections[1] && <div className="glass-panel" style={{ padding: '1.5rem' }}>
          <CheckItem id="fil_axonaut" label="Dépenses récurrentes à valider" link="https://axonaut.com/expense/dashboard" linkText="Ouvrir Axonaut" icon={<CheckCircle size={18} />} />
          <CheckItem id="fil_qonto" label="Qonto" link="/history" linkText="Suivi des dépenses" icon={<History size={18} />} />
          <CheckItem id="fil_outlook" label="Courriels" link="/history" linkText="Suivi des dépenses" icon={<Mail size={18} />} />
          <CheckItem id="fil_onedrive" label="Dossier OneDrive" link="/onedrive" linkText="Dossier Gestion" icon={<CheckCircle size={18} />} />
          
          <div style={{ marginTop: '1.25rem', paddingTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>Récupération factures (si manquant) :</span>
            <a href="https://payments.google.com/gp/w/u/0/home/activity" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: 'var(--accent)', textDecoration: 'none' }}>Google One</a>
            <a href="https://customers.securitasdirect.fr/owa-static/invoices" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: 'var(--accent)', textDecoration: 'none' }}>Verisure</a>
            <a href="https://pro.free.fr/account/billing" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: 'var(--accent)', textDecoration: 'none' }}>Free Pro</a>
            <a href="https://www.fulli.com/espace-client/factures" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: 'var(--accent)', textDecoration: 'none' }}>Fulli</a>
          </div>
        </div>}
      </div>

      <div style={{ marginBottom: '3rem' }}>
        <h4 onClick={() => toggleSection(3)} style={{ cursor: 'pointer', margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#f43f5e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{getSectionStats(3).pastille}</span> <Building size={18} style={{ flexShrink: 0 }} /> 3. Facturation fournisseurs <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>{getSectionStats(3).text}</span>
        </h4>
        {expandedSections[3] && <div className="glass-panel" style={{ padding: '1.5rem' }}>
          
          <CheckItem id="fourn_verif_depenses" label="Vérification des dépenses non rapprochées" />
          
          {(anomalies.expenses_local_error.length === 0 && anomalies.expenses_axonaut_unreconciled.length === 0) ? (
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginLeft: '2.5rem', marginBottom: '1rem' }}>✅ Toutes les dépenses sont rapprochées.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem', marginLeft: '2.5rem' }}>
              {anomalies.expenses_local_error.length > 0 && anomalies.expenses_local_error.map(exp => (
                <div key={'loc'+exp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(244, 63, 94, 0.05)', borderRadius: '6px', borderLeft: '3px solid #f43f5e' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Erreur d'envoi: {exp.fournisseur || 'Inconnu'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{exp.date ? new Date(exp.date).toLocaleDateString('fr-FR') + ' · ' : ''}{exp.montant_ttc} € - {exp.message || 'Erreur inconnue'}</div>
                  </div>
                  <button onClick={() => navigate('/history')} style={{ border: 'none', background: '#f43f5e', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}>Locales</button>
                </div>
              ))}
              {anomalies.expenses_axonaut_unreconciled.length > 0 && anomalies.expenses_axonaut_unreconciled.map(exp => (
                <div key={'axo'+exp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(245, 158, 11, 0.05)', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Non rapprochée: {exp.fournisseur || 'Inconnu'}{exp.employee ? ` — Note de frais : ${exp.employee}` : ''}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{exp.date ? new Date(exp.date).toLocaleDateString('fr-FR') + ' · ' : ''}{exp.title} - {exp.montant_ttc} €</div>
                  </div>
                  <a href="https://axonaut.com/expense/dashboard" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f59e0b', color: 'white', borderRadius: '4px', textDecoration: 'none' }}>Axonaut</a>
                </div>
              ))}
            </div>
          )}

          <CheckItem id="fourn_rapprochement" label="Rapprochement bancaire" link="https://axonaut.com/bank/dashboard" linkText="Axonaut" />
        </div>}
      </div>

      <div style={{ marginBottom: '3rem' }}>
        <h4 onClick={() => toggleSection(4)} style={{ cursor: 'pointer', margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{getSectionStats(4).pastille}</span> <Send size={18} style={{ flexShrink: 0 }} /> 4. Facturation clients <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>{getSectionStats(4).text}</span> <span style={{fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-secondary)'}}>({formatDate(lastDayOfMonth)})</span>
        </h4>
        {expandedSections[4] && <div className="glass-panel" style={{ padding: '1.5rem' }}>
          <CheckItem id="cli_refact_frais" label="Refacturation des frais" link="/rebilling" linkText="Calcul" />
          <CheckItem id="cli_suivi_temps" label="Suivi des temps" />

          {anomalies.invoices_disputed.length > 0 && (
            <>
              <h5 onClick={() => toggleSection('4_disputed')} style={{ cursor: 'pointer', margin: '1.5rem 0 0.5rem 0', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={16} color="#f43f5e" /> Factures en litige ({anomalies.invoices_disputed.length})
              </h5>
              {expandedSections['4_disputed'] !== false && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '2.5rem', marginBottom: '1rem' }}>
                  {anomalies.invoices_disputed.map(inv => (
                    <div key={'disp'+inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(244, 63, 94, 0.05)', borderRadius: '6px', borderLeft: '3px solid #f43f5e' }}>
                      <div><div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Litige en cours : {inv.company?.name || 'Inconnu'}</div><div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{inv.number} - {inv.total} € (Avoir correspondant détecté)</div></div>
                      <a href={`https://axonaut.com/adm/invoice/view/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f43f5e', color: 'white', borderRadius: '4px', textDecoration: 'none' }}>Consulter</a>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <h5 onClick={() => toggleSection('4_pending')} style={{ cursor: 'pointer', margin: '1.5rem 0 0.5rem 0', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
             <CheckSquare size={16} color="#8b5cf6" /> Relance Impayés ({anomalies.invoices_pending.length})
          </h5>
          {expandedSections['4_pending'] !== false && (
            anomalies.invoices_pending.length === 0 ? (
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginLeft: '2.5rem', marginBottom: '1rem' }}>✅ Aucun impayé détecté.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '2.5rem', marginBottom: '1rem' }}>
                {[...anomalies.invoices_pending].sort((a,b) => new Date(a.due_date || '2099-01-01') - new Date(b.due_date || '2099-01-01')).map(inv => {
                  const daysOverdue = inv.due_date ? Math.floor((new Date() - new Date(inv.due_date)) / (1000 * 60 * 60 * 24)) : 0;
                  const isOverdue = daysOverdue > 0;
                  const percent = Math.min(100, Math.max(0, isOverdue ? (daysOverdue / 60) * 100 : 0));
                  
                  // Bleu (0% retard) vers Rouge (100% retard)
                  const r = Math.round(59 + (244 - 59) * (percent / 100));
                  const g = Math.round(130 - (130 - 63) * (percent / 100));
                  const b = Math.round(246 - (246 - 94) * (percent / 100));
                  const gaugeColor = `rgb(${r}, ${g}, ${b})`;
                  
                  return (
                  <div key={'pend'+inv.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: '6px', borderLeft: '3px solid #8b5cf6', border: '1px solid var(--glass-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Impayée: {inv.company?.name || 'Inconnu'}</div>
                          {isOverdue && (
                            <div style={{ width: '60px', height: '6px', background: 'var(--glass-border)', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: '100%', height: '100%', background: gaugeColor, transition: 'background 0.3s' }}></div>
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{inv.number} - {inv.total} €</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                        <a href={`https://axonaut.com/adm/invoice/view/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#8b5cf6', color: 'white', borderRadius: '4px', textDecoration: 'none' }}>Relancer</a>
                        <div style={{ fontSize: '0.7rem', color: isOverdue ? gaugeColor : 'var(--text-secondary)', fontWeight: isOverdue ? 600 : 400 }}>Échéance: {inv.due_date ? new Date(inv.due_date).toLocaleDateString('fr-FR') : 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            )
          )}

          <h5 onClick={() => toggleSection('4_missing')} style={{ cursor: 'pointer', margin: '1.5rem 0 0.5rem 0', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
             <CheckSquare size={16} color="#f59e0b" /> Factures à émettre ({anomalies.invoices_draft.length + anomalies.invoices_missing.length})
          </h5>
          
          {expandedSections['4_missing'] !== false && (
            (anomalies.invoices_draft.length === 0 && anomalies.invoices_missing.length === 0) ? (
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginLeft: '2.5rem', marginBottom: '1rem' }}>✅ Aucun oubli ou brouillon détecté.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '2.5rem', marginBottom: '1rem' }}>
                {anomalies.invoices_missing.length > 0 && anomalies.invoices_missing.map(inv => (
                  <div key={'miss'+inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(244, 63, 94, 0.05)', borderRadius: '6px', borderLeft: '3px solid #f43f5e' }}>
                    <div><div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Abonnement manquant !</div><div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{inv.title || inv.number}</div></div>
                    <a href={`https://axonaut.com/adm/invoice/view/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f43f5e', color: 'white', borderRadius: '4px', textDecoration: 'none' }}>Axonaut</a>
                  </div>
                ))}
                {anomalies.invoices_draft.length > 0 && anomalies.invoices_draft.map(inv => (
                  <div key={'draft'+inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(245, 158, 11, 0.05)', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
                    <div><div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Brouillon non envoyé: {inv.company?.name || 'Inconnu'}</div><div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{inv.number} - {inv.total} €</div></div>
                    <a href={`https://axonaut.com/adm/invoice/view/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f59e0b', color: 'white', borderRadius: '4px', textDecoration: 'none' }}>Ouvrir</a>
                  </div>
                ))}
              </div>
            )
          )}

          <div style={{ borderBottom: '1px solid var(--glass-border)', paddingBottom: '0.5rem', marginBottom: '1rem', marginTop: '1.5rem' }}>
            <h5 
              style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-primary)', fontSize: '1.05rem', cursor: activeClients.length > 0 ? 'pointer' : 'default' }}
              onClick={activeClients.length > 0 ? toggleAllClients : undefined}
            >
              {areAllClientsChecked() ? <CheckSquare size={20} color="#10b981" /> : <Square size={20} color="var(--text-secondary)" />}
              Suivi des Clients (Commandes en cours)
            </h5>
          </div>
          {activeClients.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Aucun client avec commande en cours (devis acceptés/partiellement facturés) trouvé.</div>
          ) : (() => {
            const enCours = [...activeClients].filter(c => c.has_active_contract).sort((a, b) => a.name.localeCompare(b.name));
            const archives = [...activeClients].filter(c => !c.has_active_contract).sort((a, b) => a.name.localeCompare(b.name));
            const enCoursOpen = expandedSections['clients_active'] !== false;   // ouvert par défaut
            const archivesOpen = expandedSections['clients_archived'] === true;  // replié par défaut
            const groupHeader = (label, count, key, open) => (
              <h6 onClick={() => toggleSection(key)} style={{ cursor: 'pointer', margin: '0.25rem 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                <span style={{ width: '1rem', display: 'inline-block' }}>{open ? '▾' : '▸'}</span> {label} ({count})
              </h6>
            );
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  {groupHeader('Commandes en cours', enCours.length, 'clients_active', enCoursOpen)}
                  {enCoursOpen && (enCours.length === 0
                    ? <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '1.5rem' }}>Aucune commande en cours.</div>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>{enCours.map(renderClientRow)}</div>)}
                </div>
                <div>
                  {groupHeader('Sans commande en cours (facture impayée / archivé)', archives.length, 'clients_archived', archivesOpen)}
                  {archivesOpen && (archives.length === 0
                    ? <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '1.5rem' }}>Aucun.</div>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>{archives.map(renderClientRow)}</div>)}
                </div>
              </div>
            );
          })()}
        </div>}
      </div>

      <div style={{ marginBottom: '3rem' }}>
        <h4 onClick={() => toggleSection(5)} style={{ cursor: 'pointer', margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{getSectionStats(5).pastille}</span> <Send size={18} style={{ flexShrink: 0 }} /> 5. Envoi au comptable <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-secondary)' }}>{getSectionStats(5).text}</span>
        </h4>
        {expandedSections[5] && <div className="glass-panel" style={{ padding: '1.5rem' }}>
          <CheckItem id="qonto_statements_sent" label="Relevés bancaires envoyés au comptable" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <h5 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>Relevés Qonto</h5>
            {qontoStatements.length === 0 ? (
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Aucun relevé disponible pour ce mois.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {qontoStatements.map((stmt, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--glass-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={16} color="var(--text-secondary)" />
                      <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                        {stmt.account_name ? <strong>{stmt.account_name}</strong> : null}
                        {stmt.account_name ? ' — ' : ''}
                        {stmt.file?.file_url
                          ? <a href={stmt.file.file_url} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)', textDecoration: 'none' }}>{stmt.file?.file_name || stmt.period}</a>
                          : (stmt.file?.file_name || stmt.period)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
              <h5 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)', fontSize: '0.9rem', borderBottom: '1px solid var(--glass-border)', paddingBottom: '0.5rem' }}>Préparation de l'email</h5>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Destinataire (À)</label>
                  <input type="text" value={statementTo} onChange={(e) => setStatementTo(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>En copie (Cc)</label>
                  <input type="text" value={statementCc} onChange={(e) => setStatementCc(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Remarques additionnelles</label>
                <textarea rows="2" value={statementRemarques} onChange={(e) => setStatementRemarques(e.target.value)} placeholder="Vos remarques ici..." style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical' }}></textarea>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem' }}>
                {checklist['qonto_statements_sent'] && <span style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: 600 }}>✅ Envoyé</span>}
                <button onClick={sendStatementsToAccountant} disabled={sendingStatements || qontoStatements.length === 0} style={{ border: 'none', background: 'var(--accent)', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', cursor: sendingStatements || qontoStatements.length === 0 ? 'not-allowed' : 'pointer', opacity: sendingStatements || qontoStatements.length === 0 ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                  {sendingStatements ? <><RefreshCw size={14} className="spinning"/> Envoi en cours...</> : <><Send size={14}/> Envoyer au comptable</>}
                </button>
              </div>
            </div>
          </div>
        </div>}
      </div>


    </div>
  );
};

export default Workflow;
